/**
 * Les machines « à valider » (lot T4, registre B35) — règles PURES de l'écran du bureau.
 *
 * Le technicien a créé la fiche sur place ; le bureau la corrige, la valide, ou dit « c'est une machine existante ». Ce module
 * fabrique les textes (n° illisible, confirmation de la fusion, bilan) et choisit les machines proposées à la fusion : seules
 * les machines VALIDÉES et actives du MÊME site, jamais la fiche elle-même (le serveur refuse le reste : ceci n'est qu'un confort).
 */
import type { Asset, FusionMachine, MachineAValider } from '../api/client';

/** Le n° de série à l'écran : « illisible » quand le technicien n'a pas pu le lire (orange). */
export function numeroSerieAffiche(numero: string | null): { texte: string; illisible: boolean } {
  const n = (numero ?? '').trim();
  return n === '' ? { texte: 'illisible', illisible: true } : { texte: n, illisible: false };
}

/** Le titre d'une machine à valider dans la liste. */
export function titreMachineAValider(m: Pick<MachineAValider, 'libelle' | 'nature' | 'modele'>): string {
  return [m.libelle, m.modele].map((x) => (x ?? '').trim()).find((x) => x !== '') ?? m.nature;
}

/** Le lien « Machines à valider (N) » de l'en-tête du Parc : visible seulement quand il y en a. */
export function lienMachinesAValider(total: number): string | null {
  return total > 0 ? `Machines à valider (${total})` : null;
}

/** Recherche locale (sans accents ni majuscules) dans la liste déjà lue. */
export function filtrerMachinesAValider(liste: readonly MachineAValider[], recherche: string): MachineAValider[] {
  const cle = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const mots = cle(recherche).split(/\s+/).filter((m) => m !== '');
  if (mots.length === 0) return [...liste];
  return liste.filter((m) => {
    const texte = cle(
      [m.libelle, m.nature, m.marque?.nom, m.modele, m.numeroSerie, m.nomClient, m.site?.nom, m.client?.nom, m.creePar, m.bonOrigine?.numero]
        .filter((x): x is string => typeof x === 'string')
        .join(' '),
    );
    return mots.every((mot) => texte.includes(mot));
  });
}

/** Les machines proposées pour « c'est une machine existante » : validées, actives, du même site, jamais elle-même. */
export function ciblesDeFusion(machine: Pick<MachineAValider, 'id' | 'site'>, duSite: readonly Asset[]): Asset[] {
  if (machine.site === null) return [];
  const site = machine.site.id;
  return duSite
    .filter((a) => a.id !== machine.id && a.siteId === site && a.reviewStatus === 'VALIDATED' && a.mergedIntoAssetId === null)
    .sort((a, b) => (a.label ?? a.model ?? '').localeCompare(b.label ?? b.model ?? '', 'fr'));
}

/** La confirmation de la fusion : ce qui bouge et ce qui reste, AVANT d'agir. */
export function confirmationFusion(machine: Pick<MachineAValider, 'nature' | 'libelle' | 'modele'>, cible: Pick<Asset, 'label' | 'model'>): string {
  const nomCible = cible.label ?? cible.model ?? 'la machine choisie';
  return (
    `« ${titreMachineAValider(machine)} » est la même machine que « ${nomCible} ». ` +
    `Les interventions en cours de cette fiche passent sur « ${nomCible} » (leurs valeurs relevées aussi). ` +
    'Les bons déjà validés, les relevés et les entretiens déjà écrits restent sur la fiche en double, qui est archivée ; ' +
    `ils se lisent depuis la fiche de « ${nomCible} ». Ce geste est définitif.`
  );
}

const pluriel = (n: number, un: string, plusieurs: string) => `${n} ${n > 1 ? plusieurs : un}`;

/** Le bilan d'une fusion faite. */
export function bilanFusion(r: FusionMachine): string {
  const parties = [
    `${pluriel(r.bonsDeplaces, 'intervention déplacée', 'interventions déplacées')}`,
    `${pluriel(r.bonsRestes, 'bon resté', 'bons restés')} sur la fiche archivée`,
  ];
  if (r.relevesRestes > 0) parties.push(pluriel(r.relevesRestes, 'relevé resté', 'relevés restés'));
  if (r.entretiensRestes > 0) parties.push(pluriel(r.entretiensRestes, 'entretien resté', 'entretiens restés'));
  if (r.compteursCrees > 0) parties.push(pluriel(r.compteursCrees, 'compteur créé', 'compteurs créés'));
  let phrase = `Fusion faite : ${parties.join(', ')}.`;
  if (r.valeursRetirees > 0) {
    phrase += ` ${pluriel(r.valeursRetirees, 'valeur relevée n’a', 'valeurs relevées n’ont')} pas pu être reportée${r.valeursRetirees > 1 ? 's' : ''} (aucun compteur correspondant).`;
  }
  return phrase;
}
